import { useCallback, useSyncExternalStore } from 'react';

const KEYBOARD_CLOSE_THRESHOLD_PX = 12;

/**
 * Tracks the current on-screen keyboard inset so we can avoid relying on
 * env(keyboard-inset-height), which sticks around after the keyboard hides
 * on Mobile Safari.
 */
export function useKeyboardInset(): number {
  const subscribe = useCallback((onStoreChange: () => void) => {
    if (globalThis.window === undefined) {
      return () => {
        // Server render: no viewport available, so no unsubscribe work.
      };
    }

    const viewport = window.visualViewport;
    if (!viewport) {
      return () => {
        // VisualViewport unsupported; nothing was subscribed.
      };
    }

    const handleChange = () => {
      onStoreChange();
    };

    viewport.addEventListener('resize', handleChange);
    viewport.addEventListener('scroll', handleChange);

    return () => {
      viewport.removeEventListener('resize', handleChange);
      viewport.removeEventListener('scroll', handleChange);
    };
  }, []);

  const getSnapshot = useCallback(() => {
    if (globalThis.window === undefined) {
      return 0;
    }
    const viewport = window.visualViewport;
    if (!viewport) {
      return 0;
    }
    const heightDiff = window.innerHeight - viewport.height - viewport.offsetTop;
    if (heightDiff > KEYBOARD_CLOSE_THRESHOLD_PX) {
      return Math.round(heightDiff);
    }
    return 0;
  }, []);

  return useSyncExternalStore(subscribe, getSnapshot, () => 0);
}
